import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Divider, 
  IconButton, 
  Tooltip,
  TextField,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Stack,
  Drawer,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  TextFields as TextFieldsIcon,
  Image as ImageIcon,
  Draw as DrawIcon,
  Highlight as HighlightIcon,
  FormatShapes as ShapesIcon,
  Psychology as AIIcon,
  Search as SearchIcon,
  NavigateBefore as PrevPageIcon,
  NavigateNext as NextPageIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import PDFViewer from './PDFViewer';

// Import types from PDFViewer
type EditMode = 'text' | 'image' | 'draw' | 'highlight' | 'shape' | null;

interface Annotation {
  id: string;
  type: string;
  pageNumber: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  color?: string;
  content?: string;
  path?: { x: number; y: number }[];
}

interface PDFEditorComponentProps {
  file?: File;
  documentId?: string;
  onSave?: (data: any) => Promise<void>;
  onAIRequest?: (prompt: string, context: string) => Promise<string>;
}

const PDFEditorComponent: React.FC<PDFEditorComponentProps> = ({
  file,
  documentId,
  onSave,
  onAIRequest
}) => {
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [aiDrawerOpen, setAIDrawerOpen] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [aiResult, setAIResult] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info'}>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState<string>('pdf-file-0');
  
  // Set PDF file when prop changes
  useEffect(() => {
    if (file) {
      console.log(`PDFEditorComponent received file: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
      
      // Validate that it's actually a PDF file
      if (file.type === 'application/pdf') {
        setPdfFile(file);
        // Generate a unique key for the file to force component re-render
        setFileKey(`pdf-file-${Date.now()}`);
      } else {
        console.error(`File is not a PDF: ${file.type}`);
        setPdfError(`Invalid file type: ${file.type}. Only PDF files are supported.`);
        setPdfFile(null);
      }
    } else {
      console.log('PDFEditorComponent: No file provided');
      setPdfFile(null);
    }
  }, [file]);
  
  // Handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleEditModeSelect = (mode: EditMode) => {
    setEditMode(mode);
  };
  
  const handleAIDrawerToggle = () => {
    setAIDrawerOpen(!aiDrawerOpen);
    setAIResult(null);
    setAIPrompt('');
  };
  
  const handleSave = async () => {
    if (!onSave) return;
    
    setSaving(true);
    try {
      await onSave({
        id: documentId,
      });
      showSnackbar('Document saved successfully', 'success');
    } catch (error) {
      console.error('Error saving document:', error);
      showSnackbar('Failed to save document', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const handlePdfError = (error: string) => {
    setPdfError(error);
    showSnackbar(`PDF Error: ${error}`, 'error');
  };

  // Force reload PDF
  const handleReloadPdf = () => {
    if (pdfFile) {
      // First set to null to fully unmount the component
      setPdfFile(null);
      
      // Generate a completely new key with timestamp and random ID
      const newKey = `pdf-file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      // Clear any previous errors
      setPdfError(null);
      showSnackbar('Reloading PDF...', 'info');
      
      // Small delay to ensure clean unmount before remounting with the same file
      setTimeout(() => {
        setFileKey(newKey);
        // Use the same file reference to trigger a re-render
        setPdfFile(pdfFile);
        console.log('PDF viewer remounted with key:', newKey);
      }, 300);
    }
  };
  
  // Handle annotation added by the PDF viewer
  const handleAnnotationAdded = (annotation: Annotation) => {
    console.log('Annotation added:', annotation);
    // Here you could save the annotation to your backend
    showSnackbar(`${annotation.type} annotation added`, 'success');
  };

  // Handle annotation updated by the PDF viewer
  const handleAnnotationUpdated = (annotation: Annotation) => {
    console.log('Annotation updated:', annotation);
    // Here you could update the annotation in your backend
  };

  // Handle annotation deleted by the PDF viewer
  const handleAnnotationDeleted = (id: string) => {
    console.log('Annotation deleted:', id);
    // Here you could delete the annotation from your backend
  };

  // Image annotation
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageAnnotationPosition, setImageAnnotationPosition] = useState<{x: number, y: number} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenImageUpload = (x: number, y: number) => {
    setImageAnnotationPosition({x, y});
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageUploadCancel = () => {
    setSelectedImage(null);
    setImagePreviewUrl(null);
    setImageAnnotationPosition(null);
  };

  const handleImageUploadConfirm = () => {
    if (selectedImage && imageAnnotationPosition) {
      // Add image annotation (would be handled by the PDFViewer component)
      // Here you would call a method on the PDFViewer to add the image
      handleAnnotationAdded({
        id: `image-${Date.now()}`,
        type: 'image',
        pageNumber: 1, // Current page would come from PDFViewer
        x: imageAnnotationPosition.x,
        y: imageAnnotationPosition.y,
        content: imagePreviewUrl
      });
      
      handleImageUploadCancel(); // Clear the state after adding
    }
  };

  // State for text annotation dialog
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [textAnnotationContent, setTextAnnotationContent] = useState('');
  const [textAnnotationPosition, setTextAnnotationPosition] = useState<{x: number, y: number} | null>(null);

  // Handle text annotation dialog
  const handleOpenTextDialog = (x: number, y: number) => {
    setTextAnnotationPosition({x, y});
    setTextDialogOpen(true);
  };

  const handleCloseTextDialog = () => {
    setTextDialogOpen(false);
    setTextAnnotationContent('');
    setTextAnnotationPosition(null);
  };

  const handleAddTextAnnotation = () => {
    if (textAnnotationPosition && textAnnotationContent.trim()) {
      // Add the text annotation
      handleAnnotationAdded({
        id: `text-${Date.now()}`,
        type: 'text',
        pageNumber: 1, // Current page would come from PDFViewer
        x: textAnnotationPosition.x,
        y: textAnnotationPosition.y,
        content: textAnnotationContent
      });
      handleCloseTextDialog();
    }
  };
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {pdfFile ? pdfFile.name : 'Document Editor'}
        </Typography>
        
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ mr: 1 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            onClick={handleAIDrawerToggle}
            sx={{ mr: 1 }}
          >
            AI Assistant
          </Button>
          
          <IconButton onClick={handleMenuOpen}>
            <MoreIcon />
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download PDF</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Share Document</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      
      {/* Message explaining PDF annotation vs direct editing */}
      {pdfFile && activeTab === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">PDF Annotation Mode</Typography>
          <Typography variant="body2">
            You can annotate this PDF with text, highlights, drawings, and shapes, but direct text editing requires OCR processing.
          </Typography>
        </Alert>
      )}
      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Annotate" />
          <Tab label="Preview" />
        </Tabs>
      </Box>
      
      {/* Content */}
      <Box hidden={activeTab !== 0}>
        <Grid container spacing={2}>
          {/* Tools Sidebar */}
          <Grid item xs={12} md={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Annotation Tools
              </Typography>
              
              <Divider sx={{ my: 1 }} />
              
              <Stack spacing={1}>
                <Tooltip title="Add Text Note">
                  <Button
                    variant={editMode === 'text' ? 'contained' : 'outlined'}
                    startIcon={<TextFieldsIcon />}
                    onClick={() => handleEditModeSelect('text')}
                    fullWidth
                  >
                    Text Note
                  </Button>
                </Tooltip>
                
                <Tooltip title="Add Image">
                  <Button
                    variant={editMode === 'image' ? 'contained' : 'outlined'}
                    startIcon={<ImageIcon />}
                    onClick={() => handleEditModeSelect('image')}
                    fullWidth
                  >
                    Image
                  </Button>
                </Tooltip>
                
                <Tooltip title="Draw Annotation">
                  <Button
                    variant={editMode === 'draw' ? 'contained' : 'outlined'}
                    startIcon={<DrawIcon />}
                    onClick={() => handleEditModeSelect('draw')}
                    fullWidth
                  >
                    Draw
                  </Button>
                </Tooltip>
                
                <Tooltip title="Highlight Area">
                  <Button
                    variant={editMode === 'highlight' ? 'contained' : 'outlined'}
                    startIcon={<HighlightIcon />}
                    onClick={() => handleEditModeSelect('highlight')}
                    fullWidth
                  >
                    Highlight
                  </Button>
                </Tooltip>
                
                <Tooltip title="Add Shapes">
                  <Button
                    variant={editMode === 'shape' ? 'contained' : 'outlined'}
                    startIcon={<ShapesIcon />}
                    onClick={() => handleEditModeSelect('shape')}
                    fullWidth
                  >
                    Shapes
                  </Button>
                </Tooltip>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Note: Direct text editing requires OCR processing.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
          
          {/* Main Content */}
          <Grid item xs={12} md={10}>
            <Paper sx={{ p: 2 }}>
              {/* Controls */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box display="flex" alignItems="center">
                  <Button
                    size="small"
                    onClick={handleReloadPdf}
                    disabled={!pdfFile}
                    sx={{ mr: 1 }}
                  >
                    Reload PDF
                  </Button>
                  
                  <Tooltip title="Toggle fullscreen">
                    <IconButton>
                      <FullscreenIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                  
                <Box>
                  <IconButton 
                    onClick={() => setZoom(prev => Math.max(prev - 0.1, 0.5))}
                    disabled={zoom <= 0.5}
                  >
                    <ZoomOutIcon />
                  </IconButton>
                  
                  <Typography sx={{ mx: 2, lineHeight: '40px', display: 'inline-block' }}>
                    {Math.round(zoom * 100)}%
                  </Typography>
                  
                  <IconButton 
                    onClick={() => setZoom(prev => Math.min(prev + 0.1, 2))}
                    disabled={zoom >= 2}
                  >
                    <ZoomInIcon />
                  </IconButton>
                </Box>
              </Box>
              
              {/* Active mode indicator */}
              {editMode && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
                  <Typography>
                    Active tool: <strong>{editMode.charAt(0).toUpperCase() + editMode.slice(1)}</strong> 
                    {editMode === 'text' && " - Click on the document to add text notes"}
                    {editMode === 'draw' && " - Click and drag to draw"}
                    {editMode === 'highlight' && " - Click and drag to highlight areas"}
                    {editMode === 'image' && " - Click to insert an image"}
                    {editMode === 'shape' && " - Click to add shapes"}
                  </Typography>
                </Box>
              )}
              
              {/* PDF Viewer */}
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                {pdfError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {pdfError}
                    <Box sx={{ mt: 1 }}>
                      <Button size="small" onClick={handleReloadPdf}>
                        Try Again
                      </Button>
                    </Box>
                  </Alert>
                ) : null}
                
                {!pdfFile ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                    <Typography color="text.secondary">
                      No PDF file selected. Please upload a document first.
                    </Typography>
                  </Box>
                ) : (
                  <Box 
                    key={fileKey} 
                    sx={{ 
                      width: '100%', 
                      minHeight: 400,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <PDFViewer 
                      file={pdfFile} 
                      scale={zoom} 
                      editMode={editMode}
                      onAnnotationAdded={handleAnnotationAdded}
                      onAnnotationUpdated={handleAnnotationUpdated}
                      onAnnotationDeleted={handleAnnotationDeleted}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      
      {/* Preview Tab */}
      <Box hidden={activeTab !== 1}>
        <Paper sx={{ p: 2 }}>
          {!pdfFile ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <Typography color="text.secondary">
                No PDF file selected. Please upload a document first.
              </Typography>
            </Box>
          ) : (
            <Box 
              key={`preview-${fileKey}`} 
              sx={{ 
                width: '100%', 
                minHeight: 400,
                border: '1px solid #ddd',
                borderRadius: 1,
                overflow: 'hidden' 
              }}
            >
              <PDFViewer 
                file={pdfFile} 
                scale={zoom} 
                // No edit mode in preview tab
              />
            </Box>
          )}
        </Paper>
      </Box>
      
      {/* AI Assistant Drawer */}
      <Drawer
        anchor="right"
        open={aiDrawerOpen}
        onClose={handleAIDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 400 },
            p: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            AI Assistant
          </Typography>
          <IconButton onClick={handleAIDrawerToggle}>
            <MoreIcon />
          </IconButton>
        </Box>
        
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="What would you like the AI to do?"
          value={aiPrompt}
          onChange={(e) => setAIPrompt(e.target.value)}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => {}}
          disabled={!aiPrompt.trim() || aiLoading}
          startIcon={aiLoading ? <CircularProgress size={20} /> : <AIIcon />}
        >
          {aiLoading ? 'Processing...' : 'Generate with AI'}
        </Button>
      </Drawer>
      
      {/* Image upload input (hidden) */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleImageSelection}
      />
      
      {/* Image Preview Dialog */}
      <Dialog 
        open={!!selectedImage && !!imagePreviewUrl} 
        onClose={handleImageUploadCancel}
        maxWidth="md"
      >
        <DialogTitle>Add Image Annotation</DialogTitle>
        <DialogContent>
          {imagePreviewUrl && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <img 
                src={imagePreviewUrl} 
                alt="Selected" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '300px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImageUploadCancel}>Cancel</Button>
          <Button 
            onClick={handleImageUploadConfirm}
            variant="contained" 
            color="primary"
          >
            Add to Document
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Text Annotation Dialog */}
      <Dialog open={textDialogOpen} onClose={handleCloseTextDialog}>
        <DialogTitle>Add Text Annotation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="text-annotation"
            label="Annotation Text"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={textAnnotationContent}
            onChange={(e) => setTextAnnotationContent(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTextDialog}>Cancel</Button>
          <Button 
            onClick={handleAddTextAnnotation} 
            variant="contained" 
            color="primary"
            disabled={!textAnnotationContent.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PDFEditorComponent;
